import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Brain, Loader2, RefreshCcw, Save, X, Plus, FileText, ScanLine } from 'lucide-react';
import { toast } from 'sonner';
import { thesisApi } from '@/api/thesisApi';

const DENSITY_OPTIONS = [
  { value: 'sparse',   label: 'Sparse',   hint: '~2 citations per 1k words' },
  { value: 'moderate', label: 'Moderate', hint: '~8 citations per 1k words' },
  { value: 'dense',    label: 'Dense',    hint: '~15+ citations per 1k words' },
];

/**
 * ResearchMemoryPanel — Sprint 5 (Step 14).
 *
 * Per-project soft preferences that personalise generation prompts WITHOUT
 * training any global model. Retrieval-conditioned only.
 *
 * Features:
 *  - Auto-learned: writing style summary, citation density, abbreviations,
 *    recurring concepts (heuristic + one LLM call)
 *  - User can manually override any field
 *  - "Refresh from chapters" re-runs the extractor
 */
export default function ResearchMemoryPanel({ userId, projectId }) {
  const [loading, setLoading] = useState(true);
  const [memory, setMemory] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    if (!userId || !projectId) return;
    setLoading(true);
    try {
      const m = await thesisApi.getProjectMemory(userId, projectId);
      setMemory(m);
      setDirty(false);
    } catch (e) {
      toast.error(`Failed to load memory: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [userId, projectId]);

  useEffect(() => { load(); }, [load]);

  const refresh = async (runLlm = true) => {
    setRefreshing(true);
    try {
      const m = await thesisApi.refreshProjectMemory(userId, projectId, runLlm);
      setMemory(m);
      setDirty(false);
      toast.success(`Memory refreshed${runLlm ? ' with LLM style pass' : ''}. ${m.abbreviations?.length || 0} abbreviations, ${m.recurring_concepts?.length || 0} concepts.`);
    } catch (e) {
      toast.error(`Refresh failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRefreshing(false);
    }
  };

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      const payload = {
        preferred_style: memory.preferred_style || '',
        citation_density: memory.citation_density || 'moderate',
        abbreviations: memory.abbreviations || [],
        recurring_concepts: memory.recurring_concepts || [],
        methodology_notes: memory.methodology_notes || '',
        custom_instructions: memory.custom_instructions || '',
      };
      const m = await thesisApi.updateProjectMemory(userId, projectId, payload);
      setMemory(m);
      setDirty(false);
      toast.success('Research memory updated. Auto-refresh disabled until you re-enable it.');
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>
    );
  }
  if (!memory) {
    return <p className="text-sm text-slate-500 italic">No memory configured.</p>;
  }

  const update = (field, value) => {
    setMemory((m) => ({ ...m, [field]: value }));
    setDirty(true);
  };
  const updateAbbr = (i, field, value) => {
    const next = [...(memory.abbreviations || [])];
    next[i] = { ...next[i], [field]: value };
    update('abbreviations', next);
  };
  const removeAbbr = (i) => {
    update('abbreviations', (memory.abbreviations || []).filter((_, j) => j !== i));
  };
  const addAbbr = () => {
    update('abbreviations', [...(memory.abbreviations || []), { abbr: '', full_form: '' }]);
  };

  return (
    <Card data-testid="research-memory-panel">
      <CardContent className="p-5 space-y-5">
        <div className="flex flex-col sm:flex-row items-start justify-between gap-3 min-w-0">
          <div>
            <h2 className="text-base font-serif font-semibold flex items-center gap-2">
              <Brain className="h-4 w-4 text-blue-600" /> Research Memory
            </h2>
            <p className="text-xs text-slate-600 dark:text-slate-400 mt-1 max-w-2xl">
              Per-project soft preferences that personalise AI generation. <strong>No global model is trained on your data</strong> — these are retrieval-conditioned hints injected into prompts.
              {memory.auto_learned ? (
                <span className="ml-1 text-emerald-600 dark:text-emerald-400">Auto-learned.</span>
              ) : (
                <span className="ml-1 text-amber-600 dark:text-amber-400">Manually edited (auto-refresh disabled).</span>
              )}
              {memory.last_refreshed_at && (
                <span className="ml-1 text-slate-500">Last refreshed {new Date(memory.last_refreshed_at).toLocaleString()}.</span>
              )}
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5 sm:flex-shrink-0">
            <Button size="sm" variant="outline" onClick={() => refresh(false)} disabled={refreshing}
              data-testid="memory-refresh-fast-btn"
              title="Heuristic only — no LLM call, fast">
              <ScanLine className="h-3 w-3 mr-1" /> Fast scan
            </Button>
            <Button size="sm" variant="outline" onClick={() => refresh(true)} disabled={refreshing}
              data-testid="memory-refresh-llm-btn"
              title="Heuristic + LLM style summariser">
              {refreshing ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCcw className="h-3 w-3 mr-1" />}
              Full refresh
            </Button>
            <Button size="sm" onClick={save} disabled={!dirty || saving}
              data-testid="memory-save-btn">
              {saving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}
              Save
            </Button>
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1 block">Preferred writing style (visible to AI)</label>
          <Textarea
            data-testid="memory-style-input"
            value={memory.preferred_style || ''}
            onChange={(e) => update('preferred_style', e.target.value)}
            placeholder="e.g., concise, declarative, active voice, claim-then-evidence structure, max 30-word sentences"
            rows={3}
            className="text-sm"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1 block">Citation density</label>
            <div className="flex gap-1">
              {DENSITY_OPTIONS.map(opt => (
                <button key={opt.value} type="button"
                  onClick={() => update('citation_density', opt.value)}
                  data-testid={`memory-density-${opt.value}`}
                  className={`flex-1 text-xs px-2 py-1.5 rounded-md border transition ${memory.citation_density === opt.value ? 'bg-blue-600 text-white border-blue-600' : 'border-slate-200 dark:border-slate-700 hover:border-blue-400'}`}>
                  {opt.label}
                  <span className="block text-[9px] opacity-70">{opt.hint}</span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1 block">Methodology notes (from AI Structure Builder)</label>
            <Textarea
              data-testid="memory-methodology-input"
              value={memory.methodology_notes || ''}
              onChange={(e) => update('methodology_notes', e.target.value)}
              placeholder="Carry-over methodology notes; injected as soft hint"
              rows={3}
              className="text-sm"
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Abbreviations ({memory.abbreviations?.length || 0})</label>
            <button type="button" onClick={addAbbr}
              className="text-[10px] text-blue-600 dark:text-blue-300 hover:underline flex items-center gap-0.5"
              data-testid="memory-abbr-add-btn">
              <Plus className="h-2.5 w-2.5" /> Add
            </button>
          </div>
          <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1" data-testid="memory-abbr-list">
            {(memory.abbreviations || []).map((a, i) => (
              <div key={i} className="flex gap-1.5 items-center">
                <Input
                  data-testid={`memory-abbr-${i}-abbr`}
                  value={a.abbr || ''}
                  onChange={(e) => updateAbbr(i, 'abbr', e.target.value.toUpperCase())}
                  placeholder="AP"
                  className="w-20 text-xs font-mono uppercase"
                />
                <Input
                  data-testid={`memory-abbr-${i}-full`}
                  value={a.full_form || ''}
                  onChange={(e) => updateAbbr(i, 'full_form', e.target.value)}
                  placeholder="acute pancreatitis"
                  className="flex-1 text-xs"
                />
                <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500"
                  onClick={() => removeAbbr(i)} data-testid={`memory-abbr-${i}-remove`}>
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ))}
            {!memory.abbreviations?.length && (
              <p className="text-xs text-slate-500 italic">No abbreviations yet. Click "Fast scan" to auto-extract from your chapters.</p>
            )}
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1 block">Recurring concepts (auto-extracted)</label>
          <div className="flex flex-wrap gap-1" data-testid="memory-concepts-list">
            {(memory.recurring_concepts || []).map((c, i) => (
              <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700">{c}</span>
            ))}
            {!memory.recurring_concepts?.length && (
              <p className="text-xs text-slate-500 italic">Run "Fast scan" to extract recurring concepts.</p>
            )}
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1 block flex items-center gap-1">
            <FileText className="h-3 w-3" /> Custom instructions (free-text steering)
          </label>
          <Textarea
            data-testid="memory-custom-input"
            value={memory.custom_instructions || ''}
            onChange={(e) => update('custom_instructions', e.target.value)}
            placeholder='e.g., "Always cite Lancet guidelines for surgical decisions" · "Use UK English spelling" · "Prefer effect sizes over p-values"'
            rows={3}
            className="text-sm"
          />
        </div>

        {dirty && (
          <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
            Unsaved changes — click <strong>Save</strong> to persist.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
